import React, { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import sparkling from "./sparkling.png";
import "bootstrap/dist/css/bootstrap.min.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Acronyms = () => {
  const [topic, setTopic] = useState("");
  const [goal, setGoal] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tone, setTone] = useState(null);
  const [customTone, setCustomTone] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [country, setCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [toneOptions, setToneOptions] = useState([
    { label: "Neutral", value: "neutral", icon: "🤖" },
    { label: "Formal", value: "formal", icon: "🎩" },
    { label: "Friendly", value: "friendly", icon: "😊" },
    { label: "Casual", value: "casual", icon: "😎" },
    { label: "Professional", value: "professional", icon: "💼" },
    { label: "Diplomatic", value: "diplomatic", icon: "🤝" },
    { label: "Confident", value: "confident", icon: "💪" },
    { label: "Academic", value: "academic", icon: "🎓" },
    { label: "Vivid", value: "vivid", icon: "🎨" },
    { label: "Custom...", value: "custom", icon: "⚙️" },
  ]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get("https://countries.dev/countries");
        const countryData = response.data.map((country) => ({
          label: country.name,
          value: country.name,
          languages: Object.values(country.languages || {}).map((lang) => lang.name || lang).join(", "),
          flag: country.flags?.svg || country.flags?.png || "",
        }));
        console.log("[countries.dev] sample record:", response.data?.[0]);
        setCountries(countryData);
      } catch (error) {
        console.error("Error fetching country data:", error?.response?.status, error?.response?.data || error.message);
        toast.error("Failed to fetch country data.");
      }
    };

  

    fetchCountries();
   
  }, []);

const handleGenerateResults = async () => {
  if (!topic.trim()) {
    setError("Please write a topic before generating acronyms.");
    return;
  }

  if (!country) {
    setError("Please select a language.");
    return;
  }

  if (!tone) {
    setError("Please select a tone.");
    return;
  }

  setError("");
  setLoading(true);

  const language = country.languages || "English";

  try {
    const response = await axios({
      method: "POST",
      url: "https://tools.videoters.com/api/unified",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        model: "gpt-5.5",
        messages: [
          {
            role: "system",
            content: `You are an assistant that generates acronyms for the topic "${topic}" and the goal "${goal}". Acronyms should be written in ${language} with a ${tone.label || tone} tone.`,
          },
          {
            role: "user",
            content: `Generate exactly three distinct and meaningful acronyms for the topic "${topic}" with the goal "${goal}".

Format:

1. <ACRONYM>: <Brief Title>
<Description>

###

2. <ACRONYM>: <Brief Title>
<Description>

###

3. <ACRONYM>: <Brief Title>
<Description>

Separate each acronym with ### and return exactly three.`,
          },
        ],
        temperature: 0.7,
        max_tokens: 16384,
        stream: false,
      },
    });

    const generatedText =
      response.data?.choices?.[0]?.message?.content?.trim() || "";

    if (!generatedText) {
      toast.error("No response from API.");
      return;
    }

    let splitResults = generatedText
      .split("###")
      .map((item) => item.trim())
      .filter(Boolean);

    while (splitResults.length < 3) {
      splitResults.push(
        `Card ${splitResults.length + 1}: Placeholder description.`
      );
    }

    setResults(splitResults.slice(0, 3));
  } catch (err) {
    console.error("Full Error:", err);

    if (err.response) {
      console.log("Status:", err.response.status);
      console.log("Data:", err.response.data);
      toast.error(
        err.response.data?.error?.message || "API returned an error."
      );
    } else if (err.request) {
      console.log("Request:", err.request);
      toast.error("No response received from the server.");
    } else {
      toast.error(err.message);
    }
  } finally {
    setLoading(false);
  }
};






  const handleCustomToneSave = () => {
    if (customTone.trim()) {
      const customOption = { label: customTone, value: customTone.toLowerCase(), icon: "⚙️" };
      toneOptions.push(customOption);
      setTone(customOption);
      setShowModal(false);
      setCustomTone("");
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("Copied to clipboard!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          theme: "colored",
        });
      })
      .catch(() => {
        toast.error("Failed to copy!", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          theme: "colored",
        });
      });
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "48px",
      borderColor: state.isFocused ? "#ff5a36" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(255, 90, 54, 0.16)" : "none",
      borderRadius: "0.5rem",
      cursor: "pointer",
      "&:hover": { borderColor: state.isFocused ? "#ff5a36" : "#adb5bd" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0.25rem 0.75rem" }),
    placeholder: (base) => ({ ...base, color: "#8a94a6" }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: "#dee2e6" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "#ff5a36" : "#adb5bd",
      "&:hover": { color: "#ff5a36" },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 20,
      overflow: "hidden",
      border: "1px solid #e1e5eb",
      borderRadius: "0.75rem",
      boxShadow: "0 1rem 2.5rem rgba(30, 43, 73, 0.14)",
    }),
    option: (base, state) => ({
      ...base,
      color: state.isSelected ? "#ffffff" : "#1e2b49",
      backgroundColor: state.isSelected
        ? "#ff5a36"
        : state.isFocused
          ? "#fff0eb"
          : "#ffffff",
      cursor: "pointer",
    }),
  };

  const topicWordCount = topic.trim() ? topic.trim().split(/\s+/).length : 0;

  return (
    <div
      className="min-vh-100 py-5"
      style={{ backgroundColor: "#fff9f7", color: "#1e2b49" }}
    >
      <ToastContainer />

      <Container>
   

        <Card className="border-0 shadow rounded-4">
          <Card.Body className="p-4 p-md-5">
            <Form.Group controlId="acronymTopic" className="mb-4">
              <Form.Label className="fw-bold small">
                What is your topic? <span style={{ color: "#ff5a36" }}>*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                placeholder="e.g. Digital Marketing Strategies for 2025..."
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                rows={5}
                maxLength={500}
                className="rounded-3"
              />
              <Form.Text className="d-block text-end">
                {topicWordCount} words · {topic.length}/500 characters
              </Form.Text>
            </Form.Group>

            <Form.Group controlId="acronymGoal" className="mb-4">
              <Form.Label className="fw-bold small">What is your goal?</Form.Label>
              <Form.Control
                type="text"
                className="rounded-3 py-2"
                placeholder="e.g. To inspire the sales team..."
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
              />
            </Form.Group>

            <Row className="g-3 mb-4">
              <Col md={6}>
                <Form.Label htmlFor="toneSelect" className="fw-bold small">
                  Tone <span style={{ color: "#ff5a36" }}>*</span>
                </Form.Label>
                <Select
                  inputId="toneSelect"
                  options={toneOptions}
                  value={tone}
                  placeholder="Select a tone..."
                  styles={selectStyles}
                  onChange={(selectedOption) =>
                    selectedOption.value === "custom"
                      ? setShowModal(true)
                      : setTone(selectedOption)
                  }
                  getOptionLabel={({ label, icon }) => (
                    <div className="d-flex align-items-center gap-2">
                      <span>{icon}</span>
                      {label}
                    </div>
                  )}
                />
              </Col>

              <Col md={6}>
                <Form.Label htmlFor="countrySelect" className="fw-bold small">
                  Language <span style={{ color: "#ff5a36" }}>*</span>
                </Form.Label>
                <Select
                  inputId="countrySelect"
                  options={countries}
                  placeholder="Select language..."
                  styles={selectStyles}
                  onChange={(selected) => setCountry(selected)}
                  getOptionLabel={({ label, flag, languages }) => (
                    <div className="d-flex align-items-center gap-2">
                      <img src={flag} alt={label} width={20} height={15} />
                      <div>
                        <div>{label}</div>
                        <small className="text-muted">{languages}</small>
                      </div>
                    </div>
                  )}
                />
              </Col>
            </Row>

            {error && (
              <div className="alert alert-danger small" role="alert">
                {error}
              </div>
            )}

            <Button
              type="button"
              onClick={handleGenerateResults}
              disabled={loading}
              className="w-100 rounded-pill border-0 fw-bold py-3 d-flex align-items-center justify-content-center gap-2"
              style={{ backgroundColor: "#ff5a36" }}
            >
              {loading ? (
                <Spinner animation="border" size="sm" aria-hidden="true" />
              ) : (
                <img src={sparkling} alt="" width={18} height={18} aria-hidden="true" />
              )}
              {loading ? "Generating Acronyms..." : "Generate Acronyms"}
            </Button>
          </Card.Body>
        </Card>

        {results.length > 0 && (
          <section className="mt-4">
            <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
              <div>
                <small className="fw-bold text-uppercase" style={{ color: "#ff5a36" }}>
                  Generation complete
                </small>
                <h2 className="h4 fw-bold mb-0" style={{ color: "#1e2b49" }}>
                  Generated Acronyms
                </h2>
              </div>
              <Badge bg="light" text="dark" className="border rounded-pill px-3 py-2">
                {results.length} results
              </Badge>
            </div>

            <Row xs={1} md={3} className="g-3">
              {results.map((result, index) => {
                // Split the result into the acronym and its description
                const [acronymPart, ...descriptionParts] = result.split(":");
                const acronym = acronymPart.trim(); // The acronym itself
                const description = descriptionParts.join(":").trim(); // The rest as the description

                return (
                  <Col key={index}>
                    <Card className="h-100 border-0 shadow-sm rounded-4">
                      <Card.Body className="d-flex flex-column p-4">
                        <Badge
                          pill
                          className="align-self-start mb-3"
                          style={{ backgroundColor: "#ff5a36" }}
                        >
                          Idea {index + 1}
                        </Badge>

                        <h3 className="h5 fw-bold mb-2" style={{ color: "#1e2b49" }}>
                          {index + 1}. {acronym}
                        </h3>

                        <p className="small text-dark lh-lg mb-2">
                          {description.split(".")[0]}.
                        </p>

                        <p className="small text-secondary lh-lg mb-3">
                          {description.split(".").slice(1).join(".").trim()}
                        </p>

                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          className="rounded-pill align-self-start mt-auto px-3"
                          onClick={() => handleCopy(result)}
                        >
                          Copy
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </section>
        )}
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold" style={{ color: "#1e2b49" }}>
            Add Custom Tone
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="text"
            className="rounded-3"
            placeholder="Enter custom tone"
            value={customTone}
            onChange={(e) => setCustomTone(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            className="border-0"
            style={{ backgroundColor: "#ff5a36" }}
            onClick={handleCustomToneSave}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Acronyms;
